/**
 * EXIF-Daten vom Original in das verkleinerte Foto übernehmen.
 *
 * Ein Bild, das über ein `canvas` neu gezeichnet wird, besteht nur noch aus
 * Bildpunkten — Aufnahmezeit, Kamera und **Ort** sind weg. Für einen
 * Reisebericht ist gerade der Ort die Angabe, die man später sucht. Also wird
 * der EXIF-Block aus der Originaldatei herausgeschnitten und in das neue JPEG
 * gesetzt.
 *
 * Ohne Fremdbibliothek, weil auch das Frontend ohne Abhängigkeiten auskommen
 * soll, die keiner mehr pflegt. Gebraucht wird davon ohnehin nur ein kleiner
 * Teil: den APP1-Block finden, eine einzige Marke darin korrigieren, den Block
 * wieder einsetzen.
 *
 * Aufbau einer JPEG-Datei: `FFD8` (Anfang), dann Segmente aus `FF`, Kennung,
 * Länge (zwei Byte, die Länge zählt sich selbst mit) und Inhalt. Der
 * EXIF-Block ist das Segment `FFE1`, dessen Inhalt mit `Exif\0\0` beginnt.
 */
import type { Uhr } from './ortszeit'
import { istVerlauf, wanduhrZuUtc } from './zonen'

const SOI = 0xd8
const SOS = 0xda
const APP1 = 0xe1

export function isJpeg(bytes: Uint8Array): boolean {
  return bytes.length > 3 && bytes[0] === 0xff && bytes[1] === SOI
}

/** Sucht den EXIF-Block und gibt ihn samt Kennung und Länge zurück. */
export function findExif(jpeg: Uint8Array): Uint8Array | null {
  if (!isJpeg(jpeg)) return null

  let at = 2
  while (at + 3 < jpeg.length) {
    if (jpeg[at] !== 0xff) return null

    const marker = jpeg[at + 1]
    // Ab hier kommen die Bildpunkte; danach steht kein EXIF mehr.
    if (marker === SOS) return null

    const hoch = jpeg[at + 2]
    const niedrig = jpeg[at + 3]
    if (hoch === undefined || niedrig === undefined) return null

    const length = (hoch << 8) | niedrig
    if (length < 2) return null

    if (marker === APP1 && startsWithExif(jpeg, at + 4)) {
      return jpeg.subarray(at, at + 2 + length)
    }
    at += 2 + length
  }
  return null
}

function startsWithExif(bytes: Uint8Array, at: number): boolean {
  return (
    bytes[at] === 0x45 && // E
    bytes[at + 1] === 0x78 && // x
    bytes[at + 2] === 0x69 && // i
    bytes[at + 3] === 0x66 && // f
    bytes[at + 4] === 0x00 &&
    bytes[at + 5] === 0x00
  )
}

/**
 * Wie das Foto gedreht liegt -- `1` heißt aufrecht (Codereview D3).
 *
 * **Gebraucht für die Frage, ob ein Bild unverändert durchgehen darf.** Ein
 * JPEG unter der Kante wird nicht neu gezeichnet, sondern weitergereicht --
 * mitsamt seiner Drehmarke. Der Server liest die Maße aber aus dem
 * **Dateikopf**, und der steht bei einer Hochkantaufnahme quer: die Galerie
 * reserviert das falsche Verhältnis, und im PDF liegt das Foto auf der Seite,
 * weil dort niemand die Marke liest.
 *
 * Fehlt die Marke oder gibt es kein EXIF, ist die Antwort `1`: dann gibt es
 * nichts zu drehen, und der schnelle Weg bleibt der schnelle.
 *
 * Liest nur IFD0 -- dort steht die Ausrichtung, und `uprightExif()` daneben
 * schreibt sie an genau derselben Stelle.
 */
export function ausrichtung(jpeg: Uint8Array): number {
  const segment = findExif(jpeg)
  if (segment === null) return 1

  const tiff = 10
  if (segment.length < tiff + 8) return 1

  const little = segment[tiff] === 0x49 && segment[tiff + 1] === 0x49
  const view = new DataView(segment.buffer, segment.byteOffset, segment.byteLength)

  const ifd0 = tiff + view.getUint32(tiff + 4, little)
  if (ifd0 + 2 > segment.length) return 1

  const entries = view.getUint16(ifd0, little)
  for (let index = 0; index < entries; index++) {
    const entry = ifd0 + 2 + index * 12
    if (entry + 12 > segment.length) break
    if (view.getUint16(entry, little) === 0x0112) {
      const wert = view.getUint16(entry + 8, little)
      // Gültig sind 1 bis 8; alles andere ist kaputt und zählt als aufrecht.
      return wert >= 1 && wert <= 8 ? wert : 1
    }
  }
  return 1
}

/**
 * Setzt die Ausrichtung auf „aufrecht".
 *
 * Das Bild wurde beim Verkleinern schon gedreht — `createImageBitmap` richtet
 * es nach der EXIF-Marke aus. Übernähme man diese Marke unverändert, drehte
 * der Betrachter ein zweites Mal, und das Foto läge quer. Alles andere,
 * besonders die GPS-Angaben, bleibt unangetastet.
 */
export function uprightExif(segment: Uint8Array): Uint8Array<ArrayBuffer> {
  const copy = new Uint8Array(segment)
  // 2 Byte Kennung + 2 Byte Länge + 6 Byte "Exif\0\0" = Anfang des TIFF-Kopfs.
  const tiff = 10
  if (copy.length < tiff + 8) return copy

  const little = copy[tiff] === 0x49 && copy[tiff + 1] === 0x49
  const view = new DataView(copy.buffer, copy.byteOffset, copy.byteLength)

  // Der TIFF-Kopf verweist auf das erste Verzeichnis (IFD0).
  const ifd0 = tiff + view.getUint32(tiff + 4, little)
  if (ifd0 + 2 > copy.length) return copy

  const entries = view.getUint16(ifd0, little)
  for (let index = 0; index < entries; index++) {
    const entry = ifd0 + 2 + index * 12
    if (entry + 12 > copy.length) break

    // 0x0112 ist die Ausrichtung. Sie steht als SHORT unmittelbar im Eintrag.
    if (view.getUint16(entry, little) === 0x0112) {
      view.setUint16(entry + 8, 1, little)
      break
    }
  }
  return copy
}

/**
 * Wann und wo aufgenommen wurde.
 *
 * `at` ist eine ISO-Zeit in UTC, `lat`/`lon` sind Grad. Alles darf fehlen: ein
 * Bildschirmfoto hat keinen Ort, ein weitergeschicktes Bild oft keine Zeit.
 */
export interface Aufnahme {
  at: string | null
  lat: number | null
  lon: number | null
  /**
   * Wie weit der Leser gekommen ist — für die Oberfläche, wenn nichts kam.
   *
   * **Zwei Runden lang hat genau diese Auskunft gefehlt.** Ein ProRAW-Foto
   * (DNG) lieferte weder Ort noch Zeit, und im Formular stand pauschal „Dieses
   * Foto trägt keine Ortsangabe" — was nach einem Bild ohne Angaben aussah,
   * obwohl der Leser die Datei gar nicht erst geöffnet hatte.
   */
  befund: Befund
  /**
   * Die Uhr, auf der die Kamera stand — Minuten zu UTC.
   *
   * Aus `OffsetTimeOriginal`, das jedes iPhone seit iOS 13 schreibt. Sie ist
   * die beste Auskunft darüber, welche Zeit an diesem Ort galt: das Telefon
   * war dort, der Browser, der den Bericht später öffnet, nicht.
   *
   * `null` heißt: das Bild sagt nichts dazu.
   */
  tz_offset_min: number | null
  /**
   * The camera's wall clock (`YYYY-MM-DDTHH:mm:ss`) when the file carried a
   * time but no zone (1.87.0). It goes to the server as it is: the server
   * reads it in the trip's zone course at that moment (`TripZones`), and
   * when the course changes later, the wall clock stays and the moment
   * moves. `at` is the browser's reading of the same, for the form.
   */
  wanduhr?: string | null
}

/**
 * Was der Leser vorgefunden hat.
 *
 * `gelesen` heißt nicht „es standen Angaben darin" — es heißt „der Block war da
 * und ließ sich auseinandernehmen". Ob etwas darin stand, sagen `at` und `lat`.
 */
export type Befund =
  /** Kein Behälter, den wir kennen — ein PDF, ein kaputter Download. */
  | 'kein-bild'
  /** Bild erkannt, aber ohne EXIF-Block: gestrippt, weitergeleitet, Bildschirmfoto. */
  | 'ohne-exif'
  /** Block vorhanden, aber beim Lesen ist etwas schiefgegangen. */
  | 'unlesbar'
  | 'gelesen'

/** Woher der TIFF-Kopf kommt — nur für die Auskunft, gelesen wird gleich. */
export type Behaelter = 'jpeg' | 'tiff' | 'heif' | 'unbekannt'

/** Die Marken, die hier gebraucht werden. */
const TAG = {
  DATETIME: 0x0132,
  EXIF_IFD: 0x8769,
  GPS_IFD: 0x8825,
  DATETIME_ORIGINAL: 0x9003,
  OFFSET_TIME_ORIGINAL: 0x9011,
  GPS_LAT_REF: 0x0001,
  GPS_LAT: 0x0002,
  GPS_LON_REF: 0x0003,
  GPS_LON: 0x0004,
  GPS_TIME: 0x0007,
  GPS_DATE: 0x001d,
} as const

/** Ein Eintrag im Verzeichnis: Typ, Anzahl, und wo der Wert liegt. */
interface Eintrag {
  typ: number
  anzahl: number
  /** Ab Anfang des Blocks, nicht ab TIFF-Kopf. */
  wertAb: number
}

/** Wie viele Byte ein Wert dieses Typs belegt. */
const BREITE: Record<number, number> = { 1: 1, 2: 1, 3: 2, 4: 4, 5: 8, 7: 1, 9: 4, 10: 8 }

/**
 * Wo der TIFF-Kopf liegt — und in was für einer Datei.
 *
 * **Der Kern der Sache: es ist in allen drei Behältern derselbe Block.** EXIF
 * ist ein TIFF-Verzeichnis; nur die Verpackung unterscheidet sich.
 *
 * - **JPEG**: der Block steckt im Segment `FFE1`, hinter `Exif\0\0` — also
 *   zehn Byte nach dessen Anfang.
 * - **TIFF und DNG**: die Datei *ist* der Block. Ein DNG ist eine TIFF-Datei,
 *   und die Verweise auf den EXIF- und den GPS-Teil stehen im ersten
 *   Verzeichnis wie überall sonst. Aus dem Betrieb gemeldet: Apple Fotos gibt
 *   eine ProRAW-Aufnahme auch bei „Am kompatibelsten" als DNG heraus -- diese
 *   Einstellung wandelt HEIC nach JPEG, nicht RAW.
 * - **HEIC/HEIF**: ein ISO-BMFF-Behälter. Der Block liegt als Element darin,
 *   und wo, sagen zwei Kästen im `meta`: `iinf` nennt das Element vom Typ
 *   `Exif`, `iloc` seine Stelle.
 *
 * Gibt zurück, wo der TIFF-Kopf anfängt -- oder `null`, wenn keiner da ist.
 */
export function tiffAnfang(bytes: Uint8Array): { ab: number; art: Behaelter } | null {
  if (isJpeg(bytes)) {
    const block = findExif(bytes)
    if (block === null) return { ab: -1, art: 'jpeg' }
    // 2 Byte Kennung + 2 Byte Länge + 6 Byte "Exif\0\0".
    return { ab: block.byteOffset - bytes.byteOffset + 10, art: 'jpeg' }
  }

  if (istTiff(bytes, 0)) {
    return { ab: 0, art: 'tiff' }
  }

  if (istHeif(bytes)) {
    const ab = heifExif(bytes)
    return { ab: ab ?? -1, art: 'heif' }
  }

  return null
}

/**
 * HEIF/HEIC: ein ISO-BMFF-Behälter aus Kästen.
 *
 * Jeder Kasten ist vier Byte Länge, vier Byte Name, dann Inhalt. Eine `1` als
 * Länge heißt „die echte steht als 64-Bit dahinter", eine `0` heißt „bis zum
 * Dateiende". Der erste Kasten ist `ftyp` und nennt die Art.
 */
function istHeif(bytes: Uint8Array): boolean {
  if (bytes.length < 12) return false
  if (String.fromCharCode(bytes[4]!, bytes[5]!, bytes[6]!, bytes[7]!) !== 'ftyp') return false

  const marke = String.fromCharCode(bytes[8]!, bytes[9]!, bytes[10]!, bytes[11]!)
  return ['heic', 'heix', 'hevc', 'heim', 'heis', 'mif1', 'msf1', 'avif'].includes(marke)
}

/**
 * Wo im HEIF der EXIF-Block liegt.
 *
 * Zwei Kästen im `meta` sagen es: `iinf` nennt die Nummer des Elements vom Typ
 * `Exif`, `iloc` sagt, an welcher Stelle der Datei es steht. Der Inhalt beginnt
 * dann mit einem 4-Byte-Abstand (die Norm erlaubt einen Vorspann), und dahinter
 * steht der TIFF-Kopf.
 *
 * Gelesen wird nur so viel, wie dafür nötig ist -- die Bilddaten daneben gehen
 * uns nichts an.
 */
function heifExif(bytes: Uint8Array): number | null {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)

  /** Die Kästen einer Ebene durchgehen und den gesuchten zurückgeben. */
  const kasten = (von: number, bis: number, name: string): { ab: number; bis: number } | null => {
    let at = von
    while (at + 8 <= bis) {
      let laenge = view.getUint32(at)
      let inhalt = at + 8
      if (laenge === 1) {
        // 64-Bit-Länge. Über vier Gigabyte redet hier niemand, also reicht die
        // untere Hälfte -- die obere wäre ohnehin nicht adressierbar.
        if (at + 16 > bis) return null
        laenge = view.getUint32(at + 12)
        inhalt = at + 16
      } else if (laenge === 0) {
        laenge = bis - at
      }
      if (laenge < 8 || at + laenge > bis) return null

      const gefunden = String.fromCharCode(bytes[at + 4]!, bytes[at + 5]!, bytes[at + 6]!, bytes[at + 7]!)
      if (gefunden === name) return { ab: inhalt, bis: at + laenge }
      at += laenge
    }
    return null
  }

  const meta = kasten(0, bytes.length, 'meta')
  if (meta === null) return null
  // `meta` ist ein „full box": eine Version und drei Byte Merkmale voran.
  const inMeta = meta.ab + 4

  const iinf = kasten(inMeta, meta.bis, 'iinf')
  if (iinf === null) return null

  // Auch `iinf` ist ein full box; danach die Anzahl der Einträge (2 oder 4
  // Byte, je nach Version).
  const iinfVersion = bytes[iinf.ab]!
  const nachAnzahl = iinf.ab + 4 + (iinfVersion === 0 ? 2 : 4)

  let exifId: number | null = null
  let at = nachAnzahl
  while (at + 8 <= iinf.bis) {
    const laenge = view.getUint32(at)
    if (laenge < 8 || at + laenge > iinf.bis) break
    const name = String.fromCharCode(bytes[at + 4]!, bytes[at + 5]!, bytes[at + 6]!, bytes[at + 7]!)
    if (name === 'infe') {
      const version = bytes[at + 8]!
      // Ab Version 2 steht die Nummer als 16 Bit (v2) oder 32 Bit (v3), dann
      // der Schutzindex, dann die Art als vier Zeichen.
      const idBreite = version >= 3 ? 4 : 2
      const idAb = at + 12
      const artAb = idAb + idBreite + 2
      if (artAb + 4 <= at + laenge) {
        const art = String.fromCharCode(
          bytes[artAb]!,
          bytes[artAb + 1]!,
          bytes[artAb + 2]!,
          bytes[artAb + 3]!,
        )
        if (art === 'Exif') {
          exifId = idBreite === 4 ? view.getUint32(idAb) : view.getUint16(idAb)
          break
        }
      }
    }
    at += laenge
  }
  if (exifId === null) return null

  const iloc = kasten(inMeta, meta.bis, 'iloc')
  if (iloc === null) return null

  const ilocVersion = bytes[iloc.ab]!
  let p = iloc.ab + 4
  const groessen = view.getUint8(p)
  const basisUndIndex = view.getUint8(p + 1)
  const offsetBreite = groessen >> 4
  const laengeBreite = groessen & 0x0f
  const basisBreite = basisUndIndex >> 4
  const indexBreite = ilocVersion >= 1 ? basisUndIndex & 0x0f : 0
  p += 2

  const anzahl = ilocVersion < 2 ? view.getUint16(p) : view.getUint32(p)
  p += ilocVersion < 2 ? 2 : 4

  /** Eine Zahl von 0, 4 oder 8 Byte -- die Norm lässt alle drei zu. */
  const zahl = (ab: number, breite: number): number => {
    if (breite === 0) return 0
    if (breite === 4) return view.getUint32(ab)
    if (breite === 8) return Number(view.getBigUint64(ab))
    return view.getUint16(ab)
  }

  for (let i = 0; i < anzahl && p < iloc.bis; i++) {
    const id = ilocVersion < 2 ? view.getUint16(p) : view.getUint32(p)
    p += ilocVersion < 2 ? 2 : 4
    if (ilocVersion >= 1) p += 2 // Konstruktionsart
    p += 2 // Datenverweis
    const basis = zahl(p, basisBreite)
    p += basisBreite
    const teile = view.getUint16(p)
    p += 2

    for (let t = 0; t < teile; t++) {
      p += indexBreite
      const versatzWert = zahl(p, offsetBreite)
      p += offsetBreite
      p += laengeBreite

      if (id === exifId && t === 0) {
        // Vor dem TIFF-Kopf steht ein 4-Byte-Abstand auf einen möglichen
        // Vorspann. Fast immer ist er 0, aber gelesen wird er trotzdem.
        const ab = basis + versatzWert
        if (ab + 4 >= bytes.length) return null
        return ab + 4 + view.getUint32(ab)
      }
    }
  }
  return null
}

/** `II*\0` oder `MM\0*` -- der Kopf eines TIFF-Verzeichnisses. */
function istTiff(bytes: Uint8Array, ab: number): boolean {
  if (ab < 0 || ab + 8 > bytes.length) return false
  const little = bytes[ab] === 0x49 && bytes[ab + 1] === 0x49
  const gross = bytes[ab] === 0x4d && bytes[ab + 1] === 0x4d
  if (!little && !gross) return false
  // Die 42 dahinter -- die Norm nennt sie „die Antwort", und sie ist der
  // einzige Schutz davor, zwei zufällige Buchstaben für einen Kopf zu halten.
  return little
    ? bytes[ab + 2] === 0x2a && bytes[ab + 3] === 0x00
    : bytes[ab + 2] === 0x00 && bytes[ab + 3] === 0x2a
}

/**
 * Aufnahmeort und -zeit aus einer Bilddatei lesen.
 *
 * Gelesen wird **vor** dem Verkleinern, aus der Originaldatei — danach sind
 * die Angaben zwar noch im Block, aber der Umweg wäre unnötig.
 *
 * **Diese Funktion wirft nie.** Was sie nicht sicher lesen kann, meldet sie als
 * `null`. Ein Foto, dessen EXIF-Block krumm ist, soll trotzdem hochgehen — die
 * Angaben sind eine Zugabe, das Bild ist die Sache. Sie sagt aber, **warum**
 * nichts kam: ein stummes „nichts" hat den DNG-Fehler zwei Runden lang
 * versteckt.
 */
export function readAufnahme(bild: Uint8Array, reiseVersatz: Uhr = null): Aufnahme {
  const leer = (befund: Befund): Aufnahme => ({
    at: null,
    lat: null,
    lon: null,
    tz_offset_min: null,
    befund,
  })

  try {
    const anfang = tiffAnfang(bild)
    if (anfang === null) return leer('kein-bild')
    if (anfang.ab < 0) return leer('ohne-exif')

    const tiff = anfang.ab
    if (!istTiff(bild, tiff)) return leer('unlesbar')

    const little = bild[tiff] === 0x49 && bild[tiff + 1] === 0x49
    const view = new DataView(bild.buffer, bild.byteOffset, bild.byteLength)
    const laenge = bild.length

    const ifd0 = tiff + view.getUint32(tiff + 4, little)
    const haupt = verzeichnis(view, laenge, ifd0, tiff, little)

    const exif = zeiger(view, haupt, TAG.EXIF_IFD, little)
    const gps = zeiger(view, haupt, TAG.GPS_IFD, little)

    const exifTeil =
      exif === null ? new Map() : verzeichnis(view, laenge, tiff + exif, tiff, little)
    const gpsTeil =
      gps === null ? new Map() : verzeichnis(view, laenge, tiff + gps, tiff, little)

    const zeit = zeitpunkt(view, haupt, exifTeil, gpsTeil, little, reiseVersatz)
    return {
      at: zeit.at,
      ...ort(view, gpsTeil, little),
      tz_offset_min: versatz(view, exifTeil),
      wanduhr: zeit.wanduhr,
      befund: 'gelesen',
    }
  } catch {
    // Ein kaputter Block ist kein Grund, ein Foto abzulehnen -- aber er wird
    // gemeldet und nicht verschluckt.
    return leer('unlesbar')
  }
}

/**
 * Ein Verzeichnis einlesen.
 *
 * Jeder Eintrag ist zwölf Byte: Marke, Typ, Anzahl, Wert. Passt der Wert in
 * vier Byte, steht er unmittelbar im Eintrag; sonst steht dort ein Abstand zum
 * TIFF-Kopf. Beides wird hier auf eine Stelle im Block umgerechnet, damit die
 * Leser darunter nicht jedes Mal wieder unterscheiden müssen.
 */
function verzeichnis(
  view: DataView,
  laenge: number,
  ab: number,
  tiff: number,
  little: boolean,
): Map<number, Eintrag> {
  const eintraege = new Map<number, Eintrag>()
  if (ab + 2 > laenge) return eintraege

  const anzahl = view.getUint16(ab, little)
  for (let index = 0; index < anzahl; index++) {
    const eintrag = ab + 2 + index * 12
    if (eintrag + 12 > laenge) break

    const marke = view.getUint16(eintrag, little)
    const typ = view.getUint16(eintrag + 2, little)
    const menge = view.getUint32(eintrag + 4, little)
    const breite = BREITE[typ]
    if (breite === undefined) continue

    const bytes = breite * menge
    const wertAb = bytes <= 4 ? eintrag + 8 : tiff + view.getUint32(eintrag + 8, little)
    // Ein Abstand, der aus dem Block hinausführt, ist kaputt -- und wäre der
    // eine Fall, in dem eine Leseroutine hier über die Grenze liefe.
    if (wertAb < 0 || wertAb + bytes > laenge) continue

    eintraege.set(marke, { typ, anzahl: menge, wertAb })
  }
  return eintraege
}

/** Ein Verweis auf ein anderes Verzeichnis (LONG). */
function zeiger(
  view: DataView,
  woraus: Map<number, Eintrag>,
  marke: number,
  little: boolean,
): number | null {
  const eintrag = woraus.get(marke)
  if (eintrag === undefined || eintrag.typ !== 4) return null
  return view.getUint32(eintrag.wertAb, little)
}

/** Eine Zeichenkette, bis zur abschließenden Null. */
function text(view: DataView, woraus: Map<number, Eintrag>, marke: number): string {
  const eintrag = woraus.get(marke)
  if (eintrag === undefined || eintrag.typ !== 2) return ''

  let ergebnis = ''
  for (let index = 0; index < eintrag.anzahl; index++) {
    const zeichen = view.getUint8(eintrag.wertAb + index)
    if (zeichen === 0) break
    ergebnis += String.fromCharCode(zeichen)
  }
  return ergebnis.trim()
}

/** Ein Bruch — oder mehrere hintereinander, wie bei Grad/Minute/Sekunde. */
function brueche(
  view: DataView,
  woraus: Map<number, Eintrag>,
  marke: number,
  wieviele: number,
  little: boolean,
): number[] | null {
  const eintrag = woraus.get(marke)
  if (eintrag === undefined || eintrag.typ !== 5 || eintrag.anzahl < wieviele) return null

  const werte: number[] = []
  for (let index = 0; index < wieviele; index++) {
    const ab = eintrag.wertAb + index * 8
    const nenner = view.getUint32(ab + 4, little)
    if (nenner === 0) return null
    werte.push(view.getUint32(ab, little) / nenner)
  }
  return werte
}

/**
 * Der Aufnahmeort, in Grad.
 *
 * EXIF führt Breite und Länge als Grad, Minute, Sekunde — und die Himmels-
 * richtung getrennt daneben. Süd und West sind negativ.
 */
function ort(
  view: DataView,
  gps: Map<number, Eintrag>,
  little: boolean,
): { lat: number | null; lon: number | null } {
  const breite = brueche(view, gps, TAG.GPS_LAT, 3, little)
  const laenge = brueche(view, gps, TAG.GPS_LON, 3, little)
  if (breite === null || laenge === null) return { lat: null, lon: null }

  const lat = grad(breite) * (text(view, gps, TAG.GPS_LAT_REF).toUpperCase() === 'S' ? -1 : 1)
  const lon = grad(laenge) * (text(view, gps, TAG.GPS_LON_REF).toUpperCase() === 'W' ? -1 : 1)

  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return { lat: null, lon: null }
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return { lat: null, lon: null }
  // 0/0 liegt im Golf von Guinea und ist in der Praxis immer ein Empfänger
  // ohne Fix. Der Server wirft es ohnehin weg; hier steht es, damit die
  // Oberfläche daraus erst gar keinen Vorschlag baut.
  if (lat === 0 && lon === 0) return { lat: null, lon: null }

  return { lat: Number(lat.toFixed(6)), lon: Number(lon.toFixed(6)) }
}

function grad(werte: number[]): number {
  const [g = 0, m = 0, s = 0] = werte
  return g + m / 60 + s / 3600
}

/**
 * Der Aufnahmezeitpunkt — und die Reihenfolge ist der ganze Punkt.
 *
 * 1. **GPS-Datum und -Uhrzeit sind UTC**, ohne jede Annahme. Wo sie stehen,
 *    ist die Sache entschieden.
 * 2. `DateTimeOriginal` ist Ortszeit **ohne Zone**. Zusammen mit
 *    `OffsetTimeOriginal` (das jedes iPhone seit iOS 13 schreibt) ist sie
 *    trotzdem eindeutig.
 * 3. Bleibt nur die nackte Ortszeit, wird die Zone dieses Geräts angenommen.
 *    Das ist eine Vermutung — meistens die richtige, denn wer unterwegs Fotos
 *    schickt, hat die Uhr des Telefons dabei. Deshalb ist die Zeit im Formular
 *    immer überschreibbar.
 */
/**
 * Auf welcher Uhr die Kamera stand — `OffsetTimeOriginal` in Minuten.
 *
 * **Die beste Auskunft über die Zeit an einem Ort, die es gibt.** Das Telefon
 * war dort; der Browser, der den Bericht später öffnet, war es nicht. Steht
 * hier „+02:00", ist damit entschieden, was „14:30" an diesem Punkt bedeutet.
 *
 * Was nicht genau der Form entspricht, fällt weg: eine halb gelesene Zone wäre
 * schlechter als keine.
 */
function versatz(view: DataView, exif: Map<number, Eintrag>): number | null {
  const roh = text(view, exif, TAG.OFFSET_TIME_ORIGINAL)
  const passt = /^([+-])(\d{2}):(\d{2})$/.exec(roh)
  if (passt === null) return null

  const [, zeichen = '+', stunden = '0', minuten = '0'] = passt
  const summe = Number(stunden) * 60 + Number(minuten)
  if (!Number.isFinite(summe) || summe > 840) return null
  return zeichen === '-' ? -summe : summe
}

function zeitpunkt(
  view: DataView,
  haupt: Map<number, Eintrag>,
  exif: Map<number, Eintrag>,
  gps: Map<number, Eintrag>,
  little: boolean,
  reiseVersatz: Uhr,
): { at: string | null; wanduhr: string | null } {
  const gpsDatum = text(view, gps, TAG.GPS_DATE)
  const gpsUhr = brueche(view, gps, TAG.GPS_TIME, 3, little)
  if (gpsDatum !== '' && gpsUhr !== null) {
    const teile = gpsDatum.split(':').map(Number)
    if (teile.length === 3 && teile.every(Number.isFinite)) {
      const [jahr = 0, monat = 1, tag = 1] = teile
      const [stunde = 0, minute = 0, sekunde = 0] = gpsUhr
      const ms = Date.UTC(
        jahr,
        monat - 1,
        tag,
        Math.floor(stunde),
        Math.floor(minute),
        Math.floor(sekunde),
      )
      const gelesen = plausibel(new Date(ms))
      // GPS time is UTC: the moment is exact, no wall clock to keep.
      if (gelesen !== null) return { at: gelesen, wanduhr: null }
    }
  }

  const roh =
    text(view, exif, TAG.DATETIME_ORIGINAL) || text(view, haupt, TAG.DATETIME)
  // "2023:08:01 09:14:22" -- Doppelpunkte auch im Datum, deshalb kein Date.parse.
  const passt = /^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(roh)
  if (passt === null) return { at: null, wanduhr: null }

  const [, jahr = '', monat = '', tag = '', stunde = '', minute = '', sekunde = ''] = passt
  const zone = text(view, exif, TAG.OFFSET_TIME_ORIGINAL)
  const mitZone = /^[+-]\d{2}:\d{2}$/.test(zone) ? zone : null

  // **Ohne Zone entscheidet die Uhr der Reise, nicht die des Geräts.**
  //
  // Hier stand der örtliche `Date`-Konstruktor, und der liest die Zahlen in
  // der Zone des Browsers. Unterwegs ist das meist die richtige Vermutung --
  // in der Nachbearbeitung zu Hause ist sie um die volle Differenz falsch, und
  // das Bild landet Stunden neben seinem Platz in der Zeitleiste. Die Reise
  // weiß dagegen, wo die Kamera stand.
  //
  // With the zone course (1.87.0) the wall clock is read in the zone valid
  // at that very moment -- not the offset of today carried back over a
  // daylight saving change.
  const versatz = mitZone === null ? reiseVersatz : null
  const wanduhr = `${jahr}-${monat}-${tag}T${stunde}:${minute}:${sekunde}`
  const ohneZone = mitZone === null ? wanduhr : null

  let datum: Date
  if (mitZone !== null) {
    datum = new Date(`${wanduhr}${mitZone}`)
  } else if (istVerlauf(versatz) && versatz.length > 0) {
    datum = new Date(wanduhrZuUtc(versatz, wanduhr) ?? NaN)
  } else if (typeof versatz === 'number') {
    // Als UTC gelesen und um den Versatz zurückgedreht: dieselbe Rechnung wie
    // im Formular (`lib/ortszeit.ts`), nur andersherum.
    datum = new Date(new Date(`${wanduhr}Z`).getTime() - versatz * 60_000)
  } else {
    // Nichts sagt etwas über die Zone -- dann bleibt nur die Uhr des Geräts.
    datum = new Date(
      Number(jahr),
      Number(monat) - 1,
      Number(tag),
      Number(stunde),
      Number(minute),
      Number(sekunde),
    )
  }

  const at = plausibel(datum)
  return { at, wanduhr: at === null ? null : ohneZone }
}

/**
 * Was offensichtlich nicht stimmt, wird verworfen.
 *
 * Eine Kamera ohne Batterie fängt bei 1980 an, und ein Highlight, das im
 * letzten Jahrhundert liegt, sortiert die ganze Zeitleiste durcheinander. Ein
 * Tag Luft nach oben, damit eine Uhr, die ein paar Stunden vorgeht, nichts
 * kaputtmacht.
 */
function plausibel(datum: Date): string | null {
  const ms = datum.getTime()
  if (!Number.isFinite(ms)) return null
  if (datum.getUTCFullYear() < 1990) return null
  if (ms > Date.now() + 24 * 3600 * 1000) return null
  return datum.toISOString()
}

/**
 * Setzt einen EXIF-Block in ein JPEG ein.
 *
 * Der Block gehört unmittelbar hinter den Dateianfang. Ein bereits
 * vorhandener EXIF-Block im Ziel wird ersetzt — der Browser schreibt zwar
 * keinen, aber verlassen sollte man sich darauf nicht.
 */
export function withExif(jpeg: Uint8Array, exif: Uint8Array): Uint8Array<ArrayBuffer> {
  if (!isJpeg(jpeg)) return new Uint8Array(jpeg)

  const vorhanden = findExif(jpeg)
  const rest = vorhanden
    ? concat(
        jpeg.subarray(2, vorhanden.byteOffset - jpeg.byteOffset),
        jpeg.subarray(vorhanden.byteOffset - jpeg.byteOffset + vorhanden.length),
      )
    : jpeg.subarray(2)

  return concat(new Uint8Array([0xff, SOI]), exif, rest)
}

function concat(...teile: Uint8Array[]): Uint8Array<ArrayBuffer> {
  const gesamt = teile.reduce((summe, teil) => summe + teil.length, 0)
  const ergebnis = new Uint8Array(new ArrayBuffer(gesamt))
  let at = 0
  for (const teil of teile) {
    ergebnis.set(teil, at)
    at += teil.length
  }
  return ergebnis
}

/**
 * Einen kleinen EXIF-Block **schreiben** — aus dem, was gelesen wurde.
 *
 * **Gebraucht für alles, was kein JPEG war.** Beim Verkleinern wird der Block
 * des Originals sonst einfach übernommen; aus einem DNG geht das nicht, denn
 * dessen Verzeichnisse verweisen auf Bilddaten von vielen Megabyte, und ein
 * APP1-Segment fasst 64 kB. Also entsteht hier ein neuer, der nur das trägt,
 * worum es geht.
 *
 * Für die Anwendung selbst wäre er entbehrlich: Ort und Zeit gehen als
 * Kopfzeilen zum Server und stehen an der Datei in der Datenbank. Er steht in
 * der **Bilddatei**, und dort sucht sie jemand in zehn Jahren.
 *
 * Geschrieben wird, was eine Kamera auch schriebe:
 *
 * - **GPS-Datum und -Uhrzeit in UTC.** Sie sind die eine Angabe ohne Annahme,
 *   und unser eigener Leser nimmt sie vor allem anderen.
 * - **`DateTimeOriginal` als Ortszeit** samt `OffsetTimeOriginal`, damit jedes
 *   fremde Programm dieselbe Uhrzeit zeigt wie Apple Fotos.
 * - **Ausrichtung „aufrecht"**: gedreht wurde beim Verkleinern schon.
 */
export function baueExif(aufnahme: Aufnahme): Uint8Array<ArrayBuffer> | null {
  const hatOrt = aufnahme.lat !== null && aufnahme.lon !== null
  const moment = aufnahme.at === null ? null : new Date(aufnahme.at)
  const hatZeit = moment !== null && !Number.isNaN(moment.getTime())
  if (!hatOrt && !hatZeit) return null

  const zwei = (n: number) => String(n).padStart(2, '0')

  // --- Was in welches Verzeichnis kommt -------------------------------------
  const ifd0: Eintragung[] = [{ marke: 0x0112, typ: 3, werte: [1] }]
  const exif: Eintragung[] = []
  const gps: Eintragung[] = []

  if (hatZeit) {
    const u = moment!
    const utc =
      `${u.getUTCFullYear()}:${zwei(u.getUTCMonth() + 1)}:${zwei(u.getUTCDate())} ` +
      `${zwei(u.getUTCHours())}:${zwei(u.getUTCMinutes())}:${zwei(u.getUTCSeconds())}`

    // Ortszeit, wenn wir die Uhr kennen -- sonst bleibt es bei UTC, und das
    // sagt `OffsetTimeOriginal` dann auch.
    const versatzMin = aufnahme.tz_offset_min ?? 0
    const o = new Date(u.getTime() + versatzMin * 60_000)
    const ortszeit =
      `${o.getUTCFullYear()}:${zwei(o.getUTCMonth() + 1)}:${zwei(o.getUTCDate())} ` +
      `${zwei(o.getUTCHours())}:${zwei(o.getUTCMinutes())}:${zwei(o.getUTCSeconds())}`

    const zeichen = versatzMin < 0 ? '-' : '+'
    const roh = Math.abs(versatzMin)
    const zone = `${zeichen}${zwei(Math.floor(roh / 60))}:${zwei(roh % 60)}`

    ifd0.push({ marke: TAG.DATETIME, typ: 2, text: utc })
    exif.push({ marke: TAG.DATETIME_ORIGINAL, typ: 2, text: ortszeit })
    exif.push({ marke: TAG.OFFSET_TIME_ORIGINAL, typ: 2, text: zone })

    gps.push({
      marke: TAG.GPS_DATE,
      typ: 2,
      text: `${u.getUTCFullYear()}:${zwei(u.getUTCMonth() + 1)}:${zwei(u.getUTCDate())}`,
    })
    gps.push({
      marke: TAG.GPS_TIME,
      typ: 5,
      brueche: [
        [u.getUTCHours(), 1],
        [u.getUTCMinutes(), 1],
        [u.getUTCSeconds(), 1],
      ],
    })
  }

  if (hatOrt) {
    gps.unshift(
      { marke: TAG.GPS_LAT_REF, typ: 2, text: aufnahme.lat! < 0 ? 'S' : 'N' },
      { marke: TAG.GPS_LAT, typ: 5, brueche: alsGradBrueche(Math.abs(aufnahme.lat!)) },
      { marke: TAG.GPS_LON_REF, typ: 2, text: aufnahme.lon! < 0 ? 'W' : 'E' },
      { marke: TAG.GPS_LON, typ: 5, brueche: alsGradBrueche(Math.abs(aufnahme.lon!)) },
    )
  }

  // --- Die Verzeichnisse hintereinanderlegen --------------------------------
  //
  // Zeiger auf die beiden Untertabellen gehören in IFD0, ihre Stellen kennt man
  // aber erst, wenn die Größen feststehen. Also erst rechnen, dann schreiben.
  const groesse = (eintraege: Eintragung[]) => 2 + 12 * eintraege.length + 4
  const mitZeigern = ifd0.length + (exif.length > 0 ? 1 : 0) + (gps.length > 0 ? 1 : 0)

  const ifd0Ab = 8
  const ifd0Gross = 2 + 12 * mitZeigern + 4
  const exifAb = ifd0Ab + ifd0Gross
  const exifGross = exif.length > 0 ? groesse(exif) : 0
  const gpsAb = exifAb + exifGross
  const gpsGross = gps.length > 0 ? groesse(gps) : 0

  if (exif.length > 0) ifd0.push({ marke: TAG.EXIF_IFD, typ: 4, werte: [exifAb] })
  if (gps.length > 0) ifd0.push({ marke: TAG.GPS_IFD, typ: 4, werte: [gpsAb] })
  ifd0.sort((a, b) => a.marke - b.marke)

  const daten: number[] = []
  const datenAb = gpsAb + gpsGross

  const teil = (eintraege: Eintragung[], ab: number, gross: number): Uint8Array => {
    const raus = new Uint8Array(gross)
    const sicht = new DataView(raus.buffer)
    sicht.setUint16(0, eintraege.length, true)

    eintraege.forEach((e, i) => {
      const at = 2 + i * 12
      const roh = alsBytes(e)
      sicht.setUint16(at, e.marke, true)
      sicht.setUint16(at + 2, e.typ, true)
      sicht.setUint32(at + 4, anzahlVon(e), true)
      if (roh.length <= 4) {
        raus.set(roh, at + 8)
      } else {
        sicht.setUint32(at + 8, datenAb + daten.length, true)
        daten.push(...roh)
        // Ein Wert beginnt auf gerader Stelle -- die Norm verlangt es, und
        // manche Leser stolpern sonst.
        if (daten.length % 2 === 1) daten.push(0)
      }
    })
    // Kein weiteres Verzeichnis dahinter.
    sicht.setUint32(gross - 4, 0, true)
    void ab
    return raus
  }

  const ifd0Bytes = teil(ifd0, ifd0Ab, ifd0Gross)
  const exifBytes = exif.length > 0 ? teil(exif, exifAb, exifGross) : new Uint8Array(0)
  const gpsBytes = gps.length > 0 ? teil(gps, gpsAb, gpsGross) : new Uint8Array(0)

  const kopf = new Uint8Array(8)
  const kopfSicht = new DataView(kopf.buffer)
  kopf[0] = 0x49
  kopf[1] = 0x49
  kopfSicht.setUint16(2, 42, true)
  kopfSicht.setUint32(4, ifd0Ab, true)

  const tiff = concat(kopf, ifd0Bytes, exifBytes, gpsBytes, new Uint8Array(daten))

  // Und das Ganze als APP1-Segment verpacken: Kennung, Länge (sich selbst
  // mitzählend), „Exif\0\0", Block.
  const laenge = 2 + 6 + tiff.length
  if (laenge > 0xffff) return null
  const rahmen = new Uint8Array([0xff, APP1, laenge >> 8, laenge & 0xff, 0x45, 0x78, 0x69, 0x66, 0, 0])
  return concat(rahmen, tiff)
}

/** Ein Eintrag, wie er geschrieben werden soll. */
interface Eintragung {
  marke: number
  typ: number
  text?: string
  werte?: number[]
  brueche?: [number, number][]
}

function anzahlVon(e: Eintragung): number {
  if (e.text !== undefined) return e.text.length + 1
  if (e.brueche !== undefined) return e.brueche.length
  return e.werte?.length ?? 0
}

function alsBytes(e: Eintragung): Uint8Array {
  if (e.text !== undefined) {
    const raus = new Uint8Array(e.text.length + 1)
    for (let i = 0; i < e.text.length; i++) raus[i] = e.text.charCodeAt(i) & 0x7f
    return raus
  }
  if (e.brueche !== undefined) {
    const raus = new Uint8Array(e.brueche.length * 8)
    const sicht = new DataView(raus.buffer)
    e.brueche.forEach(([zaehler, nenner], i) => {
      sicht.setUint32(i * 8, zaehler, true)
      sicht.setUint32(i * 8 + 4, nenner, true)
    })
    return raus
  }

  const werte = e.werte ?? []
  // SHORT belegt zwei Byte, LONG vier -- mehr Typen schreibt diese Datei nicht.
  const breite = e.typ === 3 ? 2 : 4
  const raus = new Uint8Array(werte.length * breite)
  const sicht = new DataView(raus.buffer)
  werte.forEach((w, i) => {
    if (breite === 2) sicht.setUint16(i * 2, w, true)
    else sicht.setUint32(i * 4, w, true)
  })
  return raus
}

/** Grad als drei Brüche: Grad, Minute, Sekunde mit zwei Nachkommastellen. */
function alsGradBrueche(dezimal: number): [number, number][] {
  const grad = Math.floor(dezimal)
  const minutenRoh = (dezimal - grad) * 60
  const minuten = Math.floor(minutenRoh)
  const sekunden = Math.round((minutenRoh - minuten) * 60 * 100)
  return [
    [grad, 1],
    [minuten, 1],
    [sekunden, 100],
  ]
}
